import gql from 'graphql-tag';
import { graphClient } from '@services/commService';
import { getAllMentors } from '@containers/Admin/containers/UserPage/services';
import { getResourcesbyType } from '@containers/Admin/shared/ResourcesWrapper/service/resourcesService';
import { RESOURCES } from '@shared/Resources/types';

export const INVITE_FIELDS = gql`
  fragment InviteFields on Invite {
    id
    createdAt
    expiresAt
    email
    firstName
    lastName
    phoneNumber
    courseId
    mentorIds
    programExpiresAt
    comment
    status
    emailSentDates
    registrationUrl
    userAttempts {
      date
      email
      status
      additionalInfo
      userId
    }
  }
`;

export const GET_INVITES = gql`
  ${INVITE_FIELDS}
  query GetInvites(
    $sortBy: InviteSortField
    $sortOrder: SortOrderEnumType
    $filters: InviteFilters
  ) {
    invites(sortBy: $sortBy, sortOrder: $sortOrder, filters: $filters) { ...InviteFields }
  }
`;

export const CREATE_INVITE = gql`
  ${INVITE_FIELDS}
  mutation CreateInvite($input: CreateInviteInput!) {
    createInvite(input: $input) { ...InviteFields }
  }
`;

export const UPDATE_INVITE = gql`
  ${INVITE_FIELDS}
  mutation UpdateInvite($id: ID!, $input: UpdateInviteInput!) {
    updateInvite(id: $id, input: $input) { ...InviteFields }
  }
`;

export const DELETE_INVITE = gql`
  mutation DeleteInvite($id: ID!) { deleteInvite(id: $id) }
`;

export const EMAIL_INVITE = gql`
  ${INVITE_FIELDS}
  mutation EmailInvite($id: ID!) {
    emailInvite(id: $id) { ...InviteFields }
  }
`;

const mutate = (mutation, variables) => graphClient.mutate({
  mutation,
  variables,
  errorPolicy: 'all',
});

export const getInvites = async ({ offset = 0, pageSize = 10, sort, filters = {}, signal } = {}) => {
  const email = typeof filters.email === 'string' ? filters.email.trim() : '';
  const statuses = Array.isArray(filters.status)
    ? filters.status
    : filters.status ? [filters.status] : [];
  const mentorIds = Array.isArray(filters.mentorIds) ? filters.mentorIds : [];
  const result = await graphClient.query({
    query: GET_INVITES,
    variables: {
      sortBy: sort?.column || null,
      sortOrder: sort?.direction || null,
      filters: email || statuses.length || mentorIds.length ? {
        user: email || null,
        statuses: statuses.length ? statuses : null,
        mentorIds: mentorIds.length ? mentorIds : null,
      } : null,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    context: { fetchOptions: { signal } },
  });

  if (result.errors?.length) throw new Error(result.errors[0].message);

  const items = result.data?.invites || [];

  return {
    items: items.slice(offset, offset + pageSize),
    totalItems: items.length,
  };
};

export const getInviteOptions = async () => {
  const [mentorsResult, coursesResult] = await Promise.all([
    getAllMentors(),
    getResourcesbyType(RESOURCES.COURSE, 0, true, '', false),
  ]);

  const errors = [
    mentorsResult?.errors,
    coursesResult?.errors,
  ].filter(Boolean);
  if (errors.length) throw new Error('Could not load invite options.');

  return {
    mentors: mentorsResult?.allMentors || [],
    courses: coursesResult?.resources || [],
  };
};

export const createInvite = (input) => mutate(CREATE_INVITE, { input });
export const updateInvite = (id, input) => mutate(UPDATE_INVITE, { id, input });
export const deleteInvite = (id) => mutate(DELETE_INVITE, { id });
export const emailInvite = (id) => mutate(EMAIL_INVITE, { id });